import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PasienLayout from './PasienLayout';
import axiosInstance from '../../api/axios';
import './pasien-pages.css';
import { unwrapList, normalizeDoctor } from './pasien-helpers';

export default function DaftarBerobat() {
  const [doctors, setDoctors] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [pasienId, setPasienId] = useState(null);
  
  // Display text untuk input (contoh: "Selasa, 25/08/2026")
  const [displayDateText, setDisplayDateText] = useState('');
  
  const [formData, setFormData] = useState({
    doctorId: '',
    tanggalKunjungan: '', // Format YYYY-MM-DD untuk API backend
    scheduleId: '',
    keluhan: ''
  });

  const [saving, setSaving] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);

  // Load daftar dokter
  const loadDoctors = useCallback(async () => {
    setLoadingDoctors(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/pasien/dokters');
      const list = unwrapList(res.data).map(normalizeDoctor).filter((d) => d.id);
      setDoctors(list);
    } catch (err) {
      setError('Gagal memuat daftar dokter.');
    } finally {
      setLoadingDoctors(false);
    }
  }, []);

  useEffect(() => {
    const fetchPasienId = async () => {
      try {
        const res = await axiosInstance.get('/pasien/profile');
        const data = res.data?.data ?? res.data;
        const id = data?.id ?? data?.pasien_id ?? null;
        setPasienId(id);
      } catch (err) {
        console.error('Gagal ambil pasien id:', err);
      }
    };
    fetchPasienId();
    loadDoctors();
  }, [loadDoctors]);

  // Handle Pilih Dokter
  const handleDoctorChange = async (e) => {
    const id = e.target.value;
    setFormData({ doctorId: id, tanggalKunjungan: '', scheduleId: '', keluhan: '' });
    setDisplayDateText('');
    setFilteredSchedules([]);
    setBanner(null);

    if (!id) {
      setAllSchedules([]);
      return;
    }

    setLoadingSchedules(true);
    try {
      const res = await axiosInstance.get(`/pasien/dokter/${id}/jadwal`);
      const rawList = unwrapList(res.data);
      setAllSchedules(rawList);

      if (!rawList.length) {
        setBanner({ type: 'error', text: 'Dokter ini belum memiliki jadwal tersedia.' });
      }
    } catch (err) {
      setError('Gagal memuat jadwal dokter.');
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Helper konversi tanggal YYYY-MM-DD -> Hari & Format "Selasa, 25/08/2026"
  const parseSelectedDate = (dateString) => {
    if (!dateString) return { searchHari: '', formattedDisplay: '' };

    const [year, month, day] = dateString.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);

    const hariList = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const namaHari = hariList[dateObj.getDay()];

    const formattedDay = String(day).padStart(2, '0');
    const formattedMonth = String(month).padStart(2, '0');

    return {
      searchHari: namaHari.toLowerCase(),
      // 🟢 FORMAT SESUAI PERMINTAAN: "Selasa, 25/08/2026"
      formattedDisplay: `${namaHari}, ${formattedDay}/${formattedMonth}/${year}`
    };
  };

  // Handle Pilih Tanggal
  const handleDateChange = (e) => {
    const rawDateValue = e.target.value; // YYYY-MM-DD dari HTML5 date input

    if (!rawDateValue) {
      setFormData((prev) => ({ ...prev, tanggalKunjungan: '', scheduleId: '' }));
      setDisplayDateText('');
      setFilteredSchedules([]);
      return;
    }

    const { searchHari, formattedDisplay } = parseSelectedDate(rawDateValue);

    setFormData((prev) => ({ ...prev, tanggalKunjungan: rawDateValue, scheduleId: '' }));
    setDisplayDateText(formattedDisplay);
    setBanner(null);

    // Filter jadwal berdasarkan hari yang terpilih
    const matchingSchedules = allSchedules.filter((s) => {
      const rawHari = String(s.hari || s.label || s.nama_hari || '').toLowerCase();
      return rawHari.includes(searchHari);
    });

    setFilteredSchedules(matchingSchedules);

    if (!matchingSchedules.length) {
      setBanner({
        type: 'error',
        text: `Dokter tidak memiliki jadwal praktik pada hari ${searchHari.toUpperCase()}. Silakan pilih tanggal lain.`
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setBanner(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const selectedSchedule = filteredSchedules.find(s => String(s.id) === String(formData.scheduleId)) || allSchedules.find(s => String(s.id) === String(formData.scheduleId));

      const payload = {
        pasien_id: pasienId,
        dokter_id: Number(formData.doctorId),
        jadwal_dokter_id: Number(formData.scheduleId),
        tanggal_pendaftaran: formData.tanggalKunjungan, // Kirim YYYY-MM-DD ke API
        jam_kunjungan: selectedSchedule?.jam_mulai || selectedSchedule?.jam || '08:00',
        keluhan: formData.keluhan || 'Konsultasi umum',
      };

      await axiosInstance.post('/pasien/daftar', payload);
      setBanner({ type: 'success', text: 'Pendaftaran berhasil dikirim!' });
      setFormData({ doctorId: '', tanggalKunjungan: '', scheduleId: '', keluhan: '' });
      setDisplayDateText('');
      setFilteredSchedules([]);
    } catch (err) {
      setBanner({ type: 'error', text: err.response?.data?.message || 'Pendaftaran gagal.' });
    } finally {
      setSaving(false);
    }
  };

  // Set min date = Hari ini (YYYY-MM-DD)
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <PasienLayout>
      <div className="w-full p-4 md:p-6" style={{ width: '100%', boxSizing: 'border-box' }}>
        
        <header className="pasien-page-header" style={{ marginBottom: '24px' }}>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800" style={{ margin: '0 0 4px 0', color: '#1e293b' }}>
            Daftar Berobat
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
            Silakan tentukan dokter, tanggal, serta keluhan Anda.
          </p>
        </header>

        {error && (
          <div style={{ color: '#ef4444', marginBottom: '20px', padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', fontSize: '14px', fontWeight: 500 }}>
            {error}
          </div>
        )}

        {banner && (
          <div className={`pasien-banner pasien-banner--${banner.type}`} style={{ marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', fontSize: '14px', backgroundColor: banner.type === 'error' ? '#fef2f2' : '#f0fdf4', border: `1px solid ${banner.type === 'error' ? '#fee2e2' : '#bbf7d0'}`, color: banner.type === 'error' ? '#991b1b' : '#166534' }}>
            <span>{banner.text}</span>
            {banner.type === 'success' && (
              <Link to="/pasien" style={{ fontWeight: 600, color: '#4f46e5', textDecoration: 'none' }}>
                Lihat Jadwal Saya →
              </Link>
            )}
          </div>
        )}

        {loadingDoctors ? (
          <div className="loading" style={{ textAlign: 'center', padding: '60px 0', color: '#64748b', fontSize: '14px' }}>
            Memuat data dokter...
          </div>
        ) : (
          <div className="pasien-form-card" style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1.5px solid #e2e8f0', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', maxWidth: '800px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* 1. Pilih Dokter */}
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="doctorId" style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                  1. Pilih Dokter
                </label>
                <select
                  id="doctorId"
                  name="doctorId"
                  value={formData.doctorId}
                  onChange={handleDoctorChange}
                  required
                  disabled={saving}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', color: '#0f172a', backgroundColor: '#fff' }}
                >
                  <option value="">— Pilih dokter spesialis —</option>
                  {doctors.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.name} ({doc.specialty})
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Pilih Tanggal Kunjungan (Tampilan: "Selasa, 25/08/2026") */}
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="tanggalKunjungan" style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                  2. Pilih Tanggal Kunjungan
                </label>
                <input
                  id="tanggalKunjungan"
                  name="tanggalKunjungan"
                  type={displayDateText ? "text" : "date"}
                  value={displayDateText || formData.tanggalKunjungan}
                  onChange={handleDateChange}
                  onFocus={(e) => { e.target.type = "date"; }}
                  onBlur={(e) => { if (displayDateText) e.target.type = "text"; }}
                  min={todayStr}
                  placeholder="Pilih tanggal kunjungan..."
                  required
                  disabled={saving || !formData.doctorId || loadingSchedules}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    borderRadius: '8px', 
                    border: '1.5px solid #e2e8f0', 
                    outline: 'none', 
                    fontSize: '14px', 
                    color: '#0f172a', 
                    backgroundColor: formData.doctorId ? '#fff' : '#f8fafc',
                    cursor: formData.doctorId ? 'pointer' : 'not-allowed'
                  }}
                />
              </div>

              {/* 3. Sesi Praktik Dokter */}
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="scheduleId" style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                  3. Sesi Praktik Dokter
                </label>
                <select
                  id="scheduleId"
                  name="scheduleId"
                  value={formData.scheduleId}
                  onChange={handleChange}
                  required
                  disabled={saving || !formData.tanggalKunjungan || !filteredSchedules.length}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', color: '#0f172a', backgroundColor: formData.tanggalKunjungan && filteredSchedules.length ? '#fff' : '#f8fafc' }}
                >
                  <option value="">
                    {!formData.doctorId 
                      ? '— Pilih dokter terlebih dahulu —' 
                      : !formData.tanggalKunjungan 
                      ? '— Pilih tanggal terlebih dahulu —' 
                      : filteredSchedules.length 
                      ? '— Pilih Sesi Praktik —' 
                      : '— Tidak ada sesi praktik —'}
                  </option>
                  {filteredSchedules.map((s) => (
                    <option key={s.id} value={s.id}>
                      Jam Praktik: {s.jam || `${s.jam_mulai} - ${s.jam_selesai}`} WIB
                    </option>
                  ))}
                </select>
              </div>

              {/* 4. Keluhan Utama */}
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="keluhan" style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                  4. Keluhan Utama
                </label>
                <input
                  id="keluhan"
                  name="keluhan"
                  type="text"
                  placeholder="Contoh: Sakit gigi geraham sejak 2 hari lalu"
                  value={formData.keluhan}
                  onChange={handleChange}
                  disabled={saving}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '14px', color: '#0f172a' }}
                />
              </div>

              <button
                type="submit"
                className="btn-submit"
                disabled={saving || !formData.scheduleId}
                style={{ 
                  padding: '12px 20px', 
                  borderRadius: '8px', 
                  backgroundColor: saving || !formData.scheduleId ? '#94a3b8' : '#6366f1', 
                  color: '#fff', 
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: saving || !formData.scheduleId ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s',
                  marginTop: '10px',
                  textAlign: 'center'
                }}
              >
                {saving ? 'Memproses...' : 'Kirim Pendaftaran'}
              </button>
            </form>
          </div>
        )}
      </div>
    </PasienLayout>
  );
}